import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { Button, Dialog, Flex, Text } from "@radix-ui/themes";
import { useTranslation } from "react-i18next";

interface DeleteAccountDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
  isScheduled?: boolean;
  scheduledDate?: Date;
}

export const DeleteAccountDialog = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  isScheduled = false,
  scheduledDate,
}: DeleteAccountDialogProps) => {
  const { t } = useTranslation();

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Content style={{ maxWidth: 450 }}>
        <Dialog.Title>
          <Flex align="center" gap="2">
            <ExclamationTriangleIcon className="w-6 h-6 text-red-500" />
            {t("delete_account.title", "Delete Account")}
          </Flex>
        </Dialog.Title>
        <Dialog.Description size="2" className="space-y-3">
          {isScheduled ? (
            <>
              <Text className="block font-semibold text-orange-600 dark:text-orange-400">
                {t(
                  "delete_account.scheduled_warning",
                  "⏳ Your account deletion is scheduled"
                )}
              </Text>
              <Text className="block">
                {t(
                  "delete_account.scheduled_description",
                  "Your account will be permanently deleted on {{date}}. You can cancel this deletion anytime before that date.",
                  {
                    date: scheduledDate?.toLocaleString() || "unknown",
                  }
                )}
              </Text>
            </>
          ) : (
            <>
              <Text className="block font-semibold text-red-600 dark:text-red-400">
                {t(
                  "delete_account.warning",
                  "⚠️ Your account will be deleted in 48 hours"
                )}
              </Text>

              <Text className="block">
                {t(
                  "delete_account.description",
                  "All your data will be permanently deleted:"
                )}
              </Text>

              <ul className="list-disc pl-5 space-y-1">
                <li>
                  <Text size="2">
                    {t(
                      "delete_account.item_account",
                      "Your account and profile data"
                    )}
                  </Text>
                </li>
                <li>
                  <Text size="2">
                    {t("delete_account.item_models", "All your trained models")}
                  </Text>
                </li>
                <li>
                  <Text size="2">
                    {t(
                      "delete_account.item_training",
                      "All training and original pictures"
                    )}
                  </Text>
                </li>
                <li>
                  <Text size="2">
                    {t(
                      "delete_account.item_generated",
                      "All generated images and videos"
                    )}
                  </Text>
                </li>
                <li>
                  <Text size="2">
                    {t("delete_account.item_subscription", "Your subscription")}
                  </Text>
                </li>
              </ul>

              <Text className="block text-slate-600 dark:text-slate-400">
                {t(
                  "delete_account.grace_period",
                  "You will receive an email and have 48 hours to cancel this request."
                )}
              </Text>
            </>
          )}
        </Dialog.Description>

        <Flex gap="3" mt="4" justify="end">
          <Dialog.Close>
            <Button variant="soft" color="gray" disabled={isLoading}>
              {t("general.cancel", "Cancel")}
            </Button>
          </Dialog.Close>
          <Button
            variant="solid"
            color={isScheduled ? "orange" : "red"}
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading
              ? t(
                  isScheduled
                    ? "delete_account.cancelling"
                    : "delete_account.scheduling",
                  isScheduled ? "Cancelling..." : "Scheduling..."
                )
              : t(
                  isScheduled
                    ? "delete_account.cancel_deletion"
                    : "delete_account.confirm",
                  isScheduled ? "Cancel Deletion" : "Schedule Deletion"
                )}
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
};
